export type ValueField = "code" | "name" | "raceid" | "id";

export interface MasterOptionLike {
  code: string;
  name: string;
  raceid: string;
  id: string;
  specialty?: string;
}

/**
 * Map a stored demographic value (which might already be a code or might be a
 * display name) to the canonical master data code.
 */
export function mapValueToMasterCode(
  value: unknown,
  options?: MasterOptionLike[]
): string {
  return mapValueToMasterValue(value, options, "code");
}

/**
 * Map a stored demographic value to a specific field value from master data.
 * This is useful because different fields require different values to be submitted:
 * - Most fields use 'code' (e.g., ethnicity, occupation, language)
 * - Race uses 'raceid' (e.g., "2106-3")
 * - Relationship to patient uses 'id' (e.g., "1", "2")
 * - Sexual orientation and preferred pronouns use 'name' (e.g., "Straight or heterosexual")
 */
export function mapValueToMasterValue(
  value: unknown,
  options?: MasterOptionLike[],
  valueField: ValueField = "code"
): string {
  if (!value || !options || options.length === 0) {
    return "";
  }

  const str = String(value);

  // First try exact code match
  const byCode = options.find((opt) => opt.code === str);
  if (byCode) {
    return byCode[valueField];
  }

  // Try exact id match
  const byId = options.find((opt) => opt.id === str);
  if (byId) {
    return byId[valueField];
  }

  // Then try exact name match
  const byExactName = options.find((opt) => opt.name === str);
  if (byExactName) {
    return byExactName[valueField];
  }

  // Try case-insensitive name match
  const lower = str.toLowerCase().trim();
  const byName = options.find((opt) => opt.name.toLowerCase() === lower);
  if (byName) {
    return byName[valueField];
  }

  // Try partial match for name (handles cases like "Straight" -> "Straight or heterosexual")
  const byPartialName = options.find((opt) => opt.name.toLowerCase().includes(lower));
  if (byPartialName) {
    return byPartialName[valueField];
  }

  // Fall back to original string (helps with unexpected but valid codes)
  return str;
}

/**
 * Normalize a raw master-data array into {code,name} options. This keeps the
 * mapping logic in one place instead of duplicating it per type.
 */
export function normalizeMasterItems(
  items: any[] | undefined,
  config?: {
    codeKeys?: string[];
    nameKeys?: string[];
    specialtyKeys?: string[];
  }
): MasterOptionLike[] | undefined {
  if (!Array.isArray(items) || items.length === 0) {
    return undefined;
  }

  const codeKeys = config?.codeKeys ?? ["code", "id"];
  const nameKeys = config?.nameKeys ?? ["name", "description", "code"];
  const specialtyKeys = config?.specialtyKeys ?? ["specialty"];

  return items.map((item) => {
    const codeKey = codeKeys.find((k) => item[k] != null);
    const nameKey = nameKeys.find((k) => item[k] != null);
    const specialtyKey = specialtyKeys.find((k) => item[k] != null);
    const idValue = item?.id || item?.raceId || "";

    return {
      code: String(codeKey ? item[codeKey] : ""),
      name: String(nameKey ? item[nameKey] : ""),
      raceid: String(item?.raceId || item?.raceid || ""),
      id: String(idValue),
      specialty: specialtyKey ? String(item[specialtyKey]) : undefined,
    } as MasterOptionLike;
  });
}
